import { useEffect, useState } from 'react';

import { useBinaryState } from '../../../../hooks/use-binary-state';
import { initializeImport } from '../api';
import { ImportErrorCodes } from '../types/import-error-codes';

/** @typedef {import("../types/types").ImportInitRequest} ImportInitRequest */

/**
 * @param {ImportInitRequest} request
 * @param {Array<string>} attributesToHide
 * @returns {import("../types/types").InitImportReturnTypes}
 */
export const useGetInitImport = (request, attributesToHide) => {
  // The request is executed during the transition to the "create-mapping" step.
  // However, jumping between steps, e.g. going to create-config and then to create-mapping, will reset the user-created mapping.
  // This flag is intended to determine whether the request should be re-executed when we enter the create-mapping step again.
  // State is set to requireRefresh=true when a new request type is provided and requireRefresh=false when the request is already executed.
  const [requireRefresh, setRequireRefresh, setDoesNotRequireRefresh] = useBinaryState();

  // Type assertion needed because the initial value is null
  const [initError, setInitError] = useState(/** @type {string | null} */ (null));

  const [isInitImportLoading, startLoading, stopLoading] = useBinaryState();

  const [initResponse, setInitResponse] = useState({
    jobId: null,
    definition: {
      attributes: [],
      preview: [],
      columns: [],
    },
  });

  const clearInitResponse = () => {
    setInitResponse({
      jobId: null,
      definition: {
        attributes: [],
        preview: [],
        columns: [],
      },
    });
  };

  // explanation above
  // it is pretty important part of the code,
  // because it is responsible for allowing the request to be executed
  useEffect(() => {
    setRequireRefresh();
  }, [request, setRequireRefresh]);

  const hideAttributes = (data) => {
    if (attributesToHide?.length > 0) {
      const newAttributes = data?.definition?.attributes?.filter((attribute) => !attributesToHide.includes(attribute.code));
      const newData = {
        ...data,
        definition: {
          ...data.definition,
          attributes: newAttributes,
        },
      };
      return newData;
    }
    return data;
  };

  /**
   *
   * @param {() => void} executeWhenApiCallIsNotRequired
   * @param {(request: ImportInitRequest) => void} onSuccess
   * @param {(error: object) => void} onFail
   * @returns {void}
   */
  const initImport = (executeWhenApiCallIsNotRequired, onSuccess, onFail) => {
    if (!requireRefresh) {
      executeWhenApiCallIsNotRequired();
      return;
    }

    startLoading();
    initializeImport(request)
      .then((data) => {
        onSuccess(request);
        const newData = hideAttributes(data);
        setInitResponse(newData ?? data);
        setInitError(null);
        setDoesNotRequireRefresh();
      })
      .catch((error) => {
        setRequireRefresh();
        clearInitResponse();

        const errorCode = error.response.data?.code;

        if (errorCode) {
          setInitError(errorCode);
        } else {
          // This is a generic error, which is displayed when the server does not return an error code.
          // It is displayed when the server is not available or when the request is rejected.
          setInitError(ImportErrorCodes.INIT_CSV_GENERIC_ERROR);
        }

        onFail(error);
      })
      .finally(stopLoading);
  };

  return {
    ...initResponse,
    initImport,
    initError,
    isInitImportLoading,
  };
};
